import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Coins, LogOut, Search, Shield, UserRound } from "lucide-react";
import WolfSearch from "@/components/WolfSearch";
import { useGame } from "@/context/GameContext";

const links = [["/journey", "Journey"], ["/wallet", "NFT Boosts"], ["/store", "Store"], ["/leaderboard", "Leaderboard"], ["/showcase", "Showcase"], ["/gallery", "1/1 Gallery"], ["/nfts", "Get NFTs"]];

function NavSearch() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="nav-search-btn" onClick={() => setOpen(!open)} aria-label="Find a wolf" className={`p-2 rounded-full transition-colors ${open ? "text-amber-200 bg-amber-500/10" : "text-slate-400 hover:text-white"}`}>
        <Search className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed left-4 right-4 top-16 z-50 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80">
          <WolfSearch tid="nav-search" autoFocus onDone={() => setOpen(false)} className="w-full shadow-2xl" />
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const { user, logout } = useGame();
  const nav = useNavigate();
  return (
    <header data-testid="main-nav" className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/90 border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center gap-2 sm:gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src="/brand/mwp_coin.webp" alt="Wolfpack Games" data-testid="nav-coin-logo" className="w-9 h-9 sm:w-11 sm:h-11 rounded-full drop-shadow-[0_0_10px_rgba(252,211,77,0.35)] transition-transform duration-300 hover:rotate-12 hover:scale-105" />
          <span className="hidden min-[380px]:flex flex-col leading-none">
            <span data-testid="nav-game-title" className="font-display font-black tracking-[0.08em] sm:tracking-[0.12em] text-xs sm:text-base text-amber-100 whitespace-nowrap">WOLFPACK GAMES</span>
            <span data-testid="nav-tagline" className="hidden sm:block text-[9px] uppercase tracking-[0.3em] font-mono-game text-cyan-300/80 mt-1">Most Wolf Player</span>
          </span>
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1 ml-6">
            {links.map(([to, label]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) => `px-3 py-2 text-sm rounded-md transition-colors ${isActive ? "text-fuchsia-400 bg-fuchsia-500/10" : "text-slate-300 hover:text-white hover:bg-slate-800/60"}`}>
                {label}
              </NavLink>
            ))}
            {user.role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `px-3 py-2 text-sm rounded-md flex items-center gap-1 ${isActive ? "text-fuchsia-400" : "text-slate-400 hover:text-white"}`}>
                <Shield className="w-4 h-4" /> Admin
              </NavLink>
            )}
          </nav>
        )}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <NavSearch />
          {user ? (
            <>
              <Link to="/store" data-testid="nav-credits" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-200 font-mono-game text-sm">
                <Coins className="w-4 h-4" /> <span data-testid="nav-credit-balance">{(user.credits ?? 0).toLocaleString()}</span>
              </Link>
              <NavLink to="/profile" data-testid="nav-profile-btn" aria-label="My profile"
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${isActive ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-200" : "border-slate-700 text-slate-200 hover:border-amber-400/60 hover:text-white"}`}>
                <UserRound className="w-4 h-4" /> <span className="hidden sm:inline">My Profile</span>
              </NavLink>
              <button data-testid="nav-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="p-2 text-slate-400 hover:text-white" aria-label="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/showcase" data-testid="nav-public-showcase" className="hidden sm:inline text-sm text-slate-300 hover:text-white">Showcase</Link>
              <Link to="/gallery" data-testid="nav-public-gallery" className="hidden sm:inline text-sm text-slate-300 hover:text-white">1/1 Gallery</Link>
              <Link to="/nfts" data-testid="nav-public-get-nfts" className="text-sm text-amber-200 hover:text-amber-100 whitespace-nowrap">Get NFTs</Link>
              <Link to="/leaderboard" data-testid="nav-public-leaderboard" className="hidden sm:inline text-sm text-slate-300 hover:text-white">Leaderboard</Link>
              <Link to="/login" data-testid="nav-play-btn" className="ember-btn px-3 sm:px-5 py-2 rounded-full text-xs sm:text-sm whitespace-nowrap">PLAY NOW</Link>
            </>
          )}
        </div>
      </div>
      {user && (
        <nav className="md:hidden flex overflow-x-auto gap-1 px-3 pb-2">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`mnav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${isActive ? "bg-fuchsia-500/20 text-fuchsia-300" : "text-slate-400 bg-slate-900"}`}>{label}</NavLink>
          ))}
          {user.role === "admin" && <NavLink to="/admin" data-testid="mnav-admin" className="px-3 py-1.5 text-xs rounded-full text-slate-400 bg-slate-900">Admin</NavLink>}
        </nav>
      )}
    </header>
  );
}
